import type {
  HistoryReport,
  ModelComparison,
  ModelVariable,
  OutlookReport,
  Place,
  PlacesResponse,
  RadarFrames,
  WeatherReport,
} from '../../shared/contract'
import { IS_MOCK, MOCK_DELAY_MS } from '../mockMode'
import { iemRadarFrames } from '../radar'
import { inLower48Radar } from '../lib/radarCoverage'
import { retainSections } from './progress'
import { edgeReport } from './edgeReport'
import { loadEngineModule } from './engineLoader'

/** A reverse-geocoded place; `near` is how far (and which way) the point is from the name. */
export type NearPlace = Place & { near?: { distance_km: number; bearing_deg: number } | null }

/**
 * The single seam between the app and its data. Everything the UI shows comes through one
 * of these methods, returning the contract types. Two implementations:
 *   - engine: the browser-side fusion engine (Web Worker) from `web/src/engine/client.ts`
 *   - mock:   shared synthetic scenarios (`?mock=<scenario>`), always shown with the pill
 */
export interface DataSource {
  kind: 'engine' | 'mock'
  /**
   * `onEarly` (engine only) receives a first-screen report streamed ahead of the complete
   * one when the report is built from the network; see engine/index.ts.
   */
  report(latitude: number, longitude: number, onEarly?: (report: WeatherReport) => void): Promise<WeatherReport>
  /** Engine only: rebuild now (after sources timed out and have since warmed the cache). */
  refresh?(latitude: number, longitude: number): Promise<WeatherReport>
  models(latitude: number, longitude: number, variable: ModelVariable): Promise<ModelComparison>
  radar(latitude?: number, longitude?: number): Promise<RadarFrames>
  places(q: string): Promise<PlacesResponse>
  reverse(latitude: number, longitude: number): Promise<NearPlace>
  /** Time Machine: daily records for start…end (local YYYY-MM-DD, ≤ 366 days). */
  history(latitude: number, longitude: number, start: string, end: string): Promise<HistoryReport>
  /** Time Machine: ensemble days 11–16 + official CPC outlooks. */
  outlook(latitude: number, longitude: number): Promise<OutlookReport>
}

export class DataError extends Error {
  kind: 'unreachable' | 'coverage' | 'engine'
  constructor(kind: DataError['kind'], message: string) {
    super(message)
    this.kind = kind
  }
}

// ─── Mock ─────────────────────────────────────────────────────────────────────

/** Demo data lives in its own chunk (mockSource.ts), fetched only in demo mode. */
const demo = () => import('./mockSource').then((m) => m.mockSource)

export const mockSource: DataSource = {
  kind: 'mock',
  report: (lat, lon) =>
    demo().then(async (m) => {
      if (MOCK_DELAY_MS) await new Promise((r) => setTimeout(r, MOCK_DELAY_MS))
      return m.report(lat, lon)
    }),
  history: (lat, lon, start, end) => demo().then((m) => m.history(lat, lon, start, end)),
  outlook: (lat, lon) => demo().then((m) => m.outlook(lat, lon)),
  models: (lat, lon, variable) => demo().then((m) => m.models(lat, lon, variable)),
  radar: (lat, lon) => demo().then((m) => m.radar(lat, lon)),
  places: (q) => demo().then((m) => m.places(q)),
  reverse: (lat, lon) => demo().then((m) => m.reverse(lat, lon)),
}

// ─── Engine ───────────────────────────────────────────────────────────────────

interface EngineLike {
  report(latitude: number, longitude: number, onEarly?: (report: WeatherReport) => void): Promise<WeatherReport>
  refresh(latitude: number, longitude: number): Promise<WeatherReport>
  models(latitude: number, longitude: number, variable: ModelVariable): Promise<ModelComparison>
  radar(latitude?: number, longitude?: number): Promise<RadarFrames>
  places(q: string): Promise<PlacesResponse>
  reverse(latitude: number, longitude: number): Promise<NearPlace | PlacesResponse | null>
  history(latitude: number, longitude: number, start: string, end: string): Promise<HistoryReport>
  outlook(latitude: number, longitude: number): Promise<OutlookReport>
}

let enginePromise: Promise<EngineLike> | null = null

function engine(): Promise<EngineLike> {
  if (!enginePromise) {
    enginePromise = loadEngineModule()
      .then((mod) => {
        const m = mod as { getEngine?: () => EngineLike }
        if (typeof m.getEngine !== 'function') throw new Error('engine/client.ts has no getEngine()')
        return m.getEngine()
      })
      .catch((err) => {
        enginePromise = null
        throw new DataError('engine', `The forecast engine could not start: ${(err as Error).message}`)
      })
  }
  return enginePromise
}

/** Normalize engine failures into the app's error vocabulary. */
function wrap<T>(p: Promise<T>): Promise<T> {
  return p.catch((err: unknown) => {
    if (err instanceof DataError) throw err
    const msg = err instanceof Error ? err.message : String(err)
    if (/outside|coverage|not in the us|unsupported location/i.test(msg)) throw new DataError('coverage', msg)
    if (/fetch|network|failed to|offline|timeout/i.test(msg)) throw new DataError('unreachable', msg)
    throw new DataError('engine', msg)
  })
}

export const engineSource: DataSource = {
  kind: 'engine',
  report: (lat, lon, onEarly) => wrap((async () => {
    let refined = false
    let edgeReady = false
    let latest: WeatherReport | undefined
    let cached: WeatherReport | null = null
    const publishWorker = () => {
      if (!refined && latest && (!cached || /^lite\b/i.test(cached.fusion.method))) onEarly?.(latest)
    }
    // Prefer a coherent edge snapshot on cold paint, but never let an uncached or
    // unavailable endpoint hold a usable browser forecast for more than 400 ms.
    const cap = setTimeout(() => { edgeReady = true; publishWorker() }, 400)
    const edge = edgeReport(lat, lon).then((report) => {
      edgeReady = true
      clearTimeout(cap)
      cached = report
      if (!refined) {
        if (report) onEarly?.(report)
        else publishWorker()
      }
      return report
    })
    try {
      const report = await engine().then((e) => e.report(lat, lon, (partial) => {
        latest = partial
        if (edgeReady) publishWorker()
      }))
      // Paint the worker immediately, then reconcile the bounded edge request before
      // settling/persisting. A late edge can fill holes without replacing the forecast.
      if (!cached) onEarly?.(report)
      refined = true
      const snapshot = await edge
      return retainSections(snapshot ?? undefined, report)
    } catch (error) {
      refined = true
      cached ??= await edge
      if (cached) return cached
      throw error
    } finally {
      clearTimeout(cap)
    }
  })()),
  refresh: (lat, lon) => wrap(engine().then((e) => e.refresh(lat, lon))),
  models: (lat, lon, v) => wrap(engine().then((e) => e.models(lat, lon, v))),
  radar: async (lat, lon) => {
    try {
      const frames = await engine().then((e) => e.radar(lat, lon))
      if (frames.past.length || frames.future.length) return frames
    } catch {
      /* fall back to the public IEM frame list */
    }
    // The IEM list is the lower-48 composite; elsewhere an empty loop is the honest answer.
    return lat == null || lon == null || inLower48Radar(lat, lon) ? iemRadarFrames() : { generated_at: new Date().toISOString(), past: [], future: [], attribution: 'No radar available here right now.' }
  },
  places: (q) => wrap(engine().then((e) => e.places(q))),
  history: (lat, lon, start, end) => wrap(engine().then((e) => e.history(lat, lon, start, end))),
  outlook: (lat, lon) => wrap(engine().then((e) => e.outlook(lat, lon))),
  reverse: async (latitude, longitude) => {
    try {
      const res = await engine().then((e) => e.reverse(latitude, longitude))
      const place = res && 'results' in res ? res.results[0] : res
      if (place) return { ...place, latitude, longitude }
    } catch {
      /* coordinate label below */
    }
    return {
      latitude,
      longitude,
      name: 'My Location',
      region: '',
      label: `${latitude.toFixed(2)}, ${longitude.toFixed(2)}`,
      zip: null,
    }
  },
}

export const source: DataSource = IS_MOCK ? mockSource : engineSource
